import { isEmpty } from 'lodash-es';

interface Args<T> {
  /** Results the search returned for `query`. */
  data: T[];
  query: string;
  /** Whether `query` is something we could offer to create at all. */
  isOfferable?: (query: string) => boolean;
  /** Whether a result *is* `query`, rather than merely matching it. */
  isExactMatch?: (item: T, query: string) => boolean;
}

/**
 * Decides whether to offer the typed text as a new entry.
 *
 * Search matches on a substring, so results can come back without any of them
 * being what was asked for: `bob@x.com` matches an existing `alicebob@x.com`.
 * Gating the offer on "no results" therefore makes any value that is a suffix
 * of an existing one impossible to create. Gate on "no exact match" instead.
 *
 * Falls back to the "no results" rule when the caller supplies no exactness
 * test, since without one there is no way to tell the two situations apart.
 */
export default function resolveInviteOption<T>({
  data,
  query,
  isOfferable,
  isExactMatch,
}: Args<T>): string | null {
  if (!isOfferable || !isOfferable(query)) return null;

  const alreadyExists = isExactMatch
    ? (data ?? []).some((item) => isExactMatch(item, query))
    : !isEmpty(data);

  return alreadyExists ? null : query;
}
